import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { Table } from "react-bootstrap";
import { IconButton, Tooltip } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ethers } from "ethers";
import Pagination from "./Pagenation";
import "./style.css";
import { usePresale } from "../../../../scripts/usePresale";
import { useEthersProvider } from "../../../../hooks/useEthersProvider";
import {
  getCommissionTransactions,
  getPurchaseTransactions,
  getPurchaseHashes,
  getCommissionHashes,
} from "../../presaleContractFunctions";
import { request } from "../../../../admin/plugins/axios";
import { endpoints } from "../../../../utils/networks";
import { copyToClipboard } from "../../../../Helper/Helper";

const ITEMS_PER_PAGE = 10;

const TAB_CONFIG = {
  purchases: {
    label: "Purchases",
    columns: [
      { key: "hashId", label: "Hash ID" },
      { key: "date", label: "Date" },
      { key: "shares", label: "Shares" },
      { key: "amountBnb", label: "Amount (BNB)" },
    ],
  },
  commissions: {
    label: "Commissions",
    columns: [
      { key: "hashId", label: "Hash ID" },
      { key: "date", label: "Date" },
      { key: "from", label: "From" },
      { key: "to", label: "To" },
      { key: "relation", label: "Relation" },
      { key: "amountBnb", label: "Amount (BNB)" },
    ],
  },
};

const BSC_TX_BASE_URL = "https://bscscan.com/tx/";

const shortenAddress = (value) => {
  if (!value) return "--";

  let address = value;
  if (typeof value !== "string") {
    if (typeof value?.toString === "function") {
      address = value.toString();
    } else if (value?.address) {
      address = value.address;
    } else {
      return "--";
    }
  }

  if (typeof address !== "string" || address.length < 10) {
    return address || "--";
  }

  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return "--";
  const date = new Date(timestamp * 1000);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString();
};

const formatBnb = (wei) => {
  try {
    return Number(ethers.utils.formatEther(wei || "0")).toFixed(4);
  } catch (error) {
    return "0.0000";
  }
};

function TransactionsTable() {
  const { address, refreshCounter } = usePresale() || {};
  const provider = useEthersProvider();

  const [activeTab, setActiveTab] = useState("purchases");
  const [currentPages, setCurrentPages] = useState({
    purchases: 1,
    commissions: 1,
  });
  const [data, setData] = useState({
    purchases: { rows: [], total: 0 },
    commissions: { rows: [], total: 0 },
  });
  const [hashMap, setHashMap] = useState({
    purchases: {}, // { id: hash }
    commissions: {}, // { id: hash }
  });
  const hashMapRef = useRef({
    purchases: {},
    commissions: {},
  });
  const [addressNames, setAddressNames] = useState({});
  const addressNamesRef = useRef({});
  const [loading, setLoading] = useState({
    purchases: false,
    commissions: false,
  });
  const [errors, setErrors] = useState({
    purchases: "",
    commissions: "",
  });

  const currentPage = currentPages[activeTab];
  const tabConfig = TAB_CONFIG[activeTab];

  const fetchAddressNames = useCallback(async (addresses = []) => {
    if (!addresses.length) return;

    const normalized = Array.from(
      new Set(
        addresses
          .map((addr) => (typeof addr === "string" ? addr.toLowerCase() : null))
          .filter((addr) => addr && !addressNamesRef.current[addr])
      )
    );

    if (!normalized.length) return;

    try {
      const response = await request(
        endpoints.Users.GetNames.method,
        endpoints.Users.GetNames.url,
        { addresses: normalized }
      );
      const users = response?.data?.data?.users || [];
      const mapping = {};
      users.forEach((user) => {
        const lower = user?.wallet_address?.toLowerCase?.();
        if (lower) {
          mapping[lower] = user?.name || user?.username || user?.wallet_address;
        }
      });

      if (Object.keys(mapping).length > 0) {
        addressNamesRef.current = {
          ...addressNamesRef.current,
          ...mapping,
        };
        setAddressNames((prev) => ({
          ...prev,
          ...mapping,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch user names:", error);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!provider || !address) {
        console.log(`⚠️ Cannot fetch ${activeTab}:`, {
          hasProvider: !!provider,
          hasAddress: !!address,
        });
        return;
      }
      console.log(`📡 Starting fetch for ${activeTab}...`);
      setLoading((prev) => ({ ...prev, [activeTab]: true }));
      setErrors((prev) => ({ ...prev, [activeTab]: "" }));

      try {
        const pageIndex = Math.max(currentPage - 1, 0);
        console.log(`🔍 Fetching ${activeTab} transactions:`, {
          activeTab,
          address,
          pageIndex,
          itemsPerPage: ITEMS_PER_PAGE,
        });

        const response =
          activeTab === "purchases"
            ? await getPurchaseTransactions(
                provider,
                address,
                pageIndex,
                ITEMS_PER_PAGE
              )
            : await getCommissionTransactions(
                provider,
                address,
                pageIndex,
                ITEMS_PER_PAGE
              );

        console.log(`✅ ${activeTab} response received:`, response);
        console.log(`📊 ${activeTab} response structure:`, {
          hasTransactions: !!response?.transactions,
          transactionsLength: response?.transactions?.length || 0,
          total: response?.total,
          transactions: response?.transactions,
        });

        if (cancelled) return;

        // Ensure response has expected structure
        if (!response || typeof response !== "object") {
          console.error(`❌ Invalid ${activeTab} response format:`, response);
          throw new Error(`Invalid response format for ${activeTab}`);
        }

        if (!Array.isArray(response.transactions)) {
          console.error(
            `❌ ${activeTab} transactions is not an array:`,
            response.transactions
          );
          throw new Error(`Transactions is not an array for ${activeTab}`);
        }

        if (activeTab === "purchases") {
          console.log(
            "📊 Purchase Transactions - Raw Response from Contract:",
            response
          );
          console.log(
            "📊 Purchase Transactions - Raw Transactions Array:",
            response.transactions
          );
          console.log(
            "📊 Purchase Transactions - Total Count:",
            response.total
          );
        } else if (activeTab === "commissions") {
          console.log(
            "💰 Commission Transactions - Raw Response from Contract:",
            response
          );
          console.log(
            "💰 Commission Transactions - Raw Transactions Array:",
            response.transactions
          );
          console.log(
            "💰 Commission Transactions - Total Count:",
            response.total
          );
        }

        const normalizedRows =
          activeTab === "purchases"
            ? response.transactions.map((tx, index) => {
                const txId = Number(tx.id) || 0;
                return {
                  id: tx.id || "0",
                  hashId:
                    hashMapRef.current.purchases[txId] ||
                    hashMap.purchases[txId] ||
                    "Loading...",
                  date: formatDateTime(tx.timestamp),
                  shares: tx.shares || "0",
                  amountBnb: formatBnb(tx.amount),
                  // Keep raw data for console logging
                  rawData: tx,
                };
              })
            : response.transactions.map((tx, index) => {
                const txId = Number(tx.id) || 0;
                return {
                  id: tx.id || "0",
                  hashId:
                    hashMapRef.current.commissions[txId] ||
                    hashMap.commissions[txId] ||
                    "Loading...",
                  date: formatDateTime(tx.timestamp),
                  from: shortenAddress(tx.from),
                  fromFull: tx.from,
                  to: shortenAddress(tx.to),
                  toFull: tx.to,
                  relation: tx.relation || 0,
                  amountBnb: formatBnb(tx.amount),
                  // Keep raw data for console logging
                  rawData: tx,
                };
              });

        if (activeTab === "commissions" && response.transactions.length > 0) {
          const addressList = response.transactions
            .flatMap((tx) => [tx.from, tx.to])
            .filter((addr) => typeof addr === "string");
          fetchAddressNames(addressList);
        }

        // Fetch hash IDs from API
        if (activeTab === "purchases" && response.transactions.length > 0) {
          const purchaseIds = response.transactions
            .map((tx) => Number(tx.id))
            .filter((id) => id > 0 && !hashMapRef.current.purchases[id]);

          if (purchaseIds.length > 0) {
            getPurchaseHashes(purchaseIds)
              .then((result) => {
                console.log("📥 Purchase hashes API response:", result);
                const newHashMap = { ...hashMapRef.current.purchases };

                // Handle response format: {"1": "0x...", "2": "0x..."}
                if (Array.isArray(result)) {
                  result.forEach((item) => {
                    const transactionId = Number(item?.transaction_id);
                    const hash = item?.transaction_hash;
                    if (transactionId && hash) {
                      newHashMap[transactionId] = hash;
                    }
                  });
                } else if (result && typeof result === "object") {
                  Object.keys(result).forEach((key) => {
                    const transactionId = Number(key);
                    const hash = result[key];
                    if (transactionId && hash) {
                      newHashMap[transactionId] = hash;
                    }
                  });
                }

                hashMapRef.current.purchases = newHashMap;
                setHashMap((prev) => ({
                  ...prev,
                  purchases: { ...newHashMap },
                }));
              })
              .catch((error) => {
                console.error("Failed to fetch purchase hashes:", error);
              });
          }
        } else if (
          activeTab === "commissions" &&
          response.transactions.length > 0
        ) {
          const commissionIds = response.transactions
            .map((tx) => Number(tx.id))
            .filter((id) => id > 0 && !hashMapRef.current.commissions[id]);

          if (commissionIds.length > 0) {
            getCommissionHashes(commissionIds)
              .then((result) => {
                console.log("📥 Commission hashes API response:", result);
                const newHashMap = { ...hashMapRef.current.commissions };

                // Handle response format: {"1": "0x...", "2": "0x..."}
                if (Array.isArray(result)) {
                  result.forEach((item) => {
                    const transactionId = Number(item?.transaction_id);
                    const hash = item?.transaction_hash;
                    if (transactionId && hash) {
                      newHashMap[transactionId] = hash;
                    }
                  });
                } else if (result && typeof result === "object") {
                  Object.keys(result).forEach((key) => {
                    const transactionId = Number(key);
                    const hash = result[key];
                    if (transactionId && hash) {
                      newHashMap[transactionId] = hash;
                    }
                  });
                }

                hashMapRef.current.commissions = newHashMap;
                setHashMap((prev) => ({
                  ...prev,
                  commissions: { ...newHashMap },
                }));
              })
              .catch((error) => {
                console.error("Failed to fetch commission hashes:", error);
              });
          }
        }

        if (activeTab === "purchases") {
          console.log(
            "📊 Purchase Transactions - Processed Table Data:",
            normalizedRows
          );
          console.log("📊 Purchase Transactions - Full Table Data Object:", {
            rows: normalizedRows,
            total: response.total || 0,
            page: currentPage,
            pageIndex: pageIndex,
            itemsPerPage: ITEMS_PER_PAGE,
          });

          // Print each transaction details
          normalizedRows.forEach((row, index) => {
            console.log(`📊 Purchase Transaction #${index + 1}:`, {
              txId: row.txId,
              date: row.date,
              shares: row.shares,
              amountBnb: row.amountBnb,
              rawData: row.rawData,
            });
          });
        }

        if (activeTab === "purchases") {
          const finalData = {
            rows: normalizedRows,
            total: response.total || 0,
          };
          console.log(
            "📊 Purchase Transactions - Final State Data:",
            finalData
          );
        } else if (activeTab === "commissions") {
          const finalData = {
            rows: normalizedRows,
            total: response.total || 0,
          };
          console.log(
            "💰 Commission Transactions - Final State Data:",
            finalData
          );
          // Print each commission transaction details
          normalizedRows.forEach((row, index) => {
            console.log(`💰 Commission Transaction #${index + 1}:`, {
              id: row.id,
              date: row.date,
              from: row.from,
              fromFull: row.fromFull,
              to: row.to,
              toFull: row.toFull,
              amountBnb: row.amountBnb,
              hashId: row.hashId,
              rawData: row.rawData,
            });
          });
        }

        setData((prev) => ({
          ...prev,
          [activeTab]: {
            rows: normalizedRows,
            total: response.total || 0,
          },
        }));

        const totalPages = Math.max(
          1,
          Math.ceil((response.total || 0) / ITEMS_PER_PAGE)
        );
        if (currentPage > totalPages) {
          setCurrentPages((prev) => ({
            ...prev,
            [activeTab]: totalPages,
          }));
        }
      } catch (error) {
        console.error(`Failed to load ${activeTab} transactions:`, error);
        console.error("Error details:", {
          activeTab,
          address,
          provider: !!provider,
          pageIndex: currentPage - 1,
          limit: ITEMS_PER_PAGE,
          errorMessage: error?.message,
          errorReason: error?.reason,
          errorCode: error?.code,
        });
        if (!cancelled) {
          setErrors((prev) => ({
            ...prev,
            [activeTab]: `Failed to load ${activeTab} from the contract: ${
              error?.message || error?.reason || "Unknown error"
            }`,
          }));
        }
      } finally {
        if (!cancelled) {
          setLoading((prev) => ({ ...prev, [activeTab]: false }));
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    provider,
    address,
    activeTab,
    currentPage,
    refreshCounter,
    fetchAddressNames,
  ]);

  // Update rows when hashMap changes
  useEffect(() => {
    setData((prev) => {
      const currentData = prev[activeTab];
      if (!currentData || !currentData.rows) return prev;

      const updatedRows = currentData.rows.map((row) => {
        const rowId = Number(row.id) || 0;
        const hashId =
          activeTab === "purchases"
            ? hashMapRef.current.purchases[rowId] ||
              hashMap.purchases[rowId] ||
              row.hashId
            : hashMapRef.current.commissions[rowId] ||
              hashMap.commissions[rowId] ||
              row.hashId;

        return { ...row, hashId };
      });

      return {
        ...prev,
        [activeTab]: {
          ...currentData,
          rows: updatedRows,
        },
      };
    });
  }, [hashMap, activeTab]);

  const totalPages = useMemo(() => {
    const total = data[activeTab].total || 0;
    return Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
  }, [data, activeTab]);

  const paginationSetPage = useCallback(
    (valueOrUpdater) => {
      setCurrentPages((prev) => {
        const previousPage = prev[activeTab] || 1;
        const nextPageRaw =
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(previousPage)
            : valueOrUpdater;
        const nextPage = Math.max(1, Number(nextPageRaw) || 1);
        if (nextPage === previousPage) {
          return prev;
        }
        return {
          ...prev,
          [activeTab]: nextPage,
        };
      });
    },
    [activeTab]
  );

  const rows = data[activeTab].rows;
  const isLoading = loading[activeTab];
  const errorMessage = errors[activeTab];

  return (
    <div className="shared-space">
      <div className="CommissionsDetails">
        <h2 className="table-label">Transactions</h2>
        <div className="transactions-filter-buttons mb-3 table-label totalCommission">
          {Object.entries(TAB_CONFIG).map(([key, config]) => (
            <button
              key={key}
              type="button"
              className={`transaction-filter-btn ${
                activeTab === key ? "active" : ""
              }`}
              onClick={() => setActiveTab(key)}
            >
              {config.label}
            </button>
          ))}
          <button
            type="button"
            className="transaction-filter-btn disabled"
            disabled
          >
            Profit
          </button>
          <button
            type="button"
            className="transaction-filter-btn disabled"
            disabled
          >
            Distribution
          </button>
        </div>
        <div className="borderSpace"></div>
      </div>

      <div className="bg-live">
        <div className="table-responsive-wrapper">
        <Table className="purchasing-table">
          <thead>
            <tr>
              {tabConfig.columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!address ? (
              <tr>
                <td
                  className="table-no-data"
                  colSpan={tabConfig.columns.length}
                >
                  Connect your wallet to view transactions.
                </td>
              </tr>
            ) : isLoading ? (
              <tr>
                <td
                  className="table-no-data"
                  colSpan={tabConfig.columns.length}
                >
                  Loading...
                </td>
              </tr>
            ) : errorMessage ? (
              <tr>
                <td
                  className="table-no-data"
                  colSpan={tabConfig.columns.length}
                >
                  {errorMessage}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  className="table-no-data"
                  colSpan={tabConfig.columns.length}
                >
                  No transactions found.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${activeTab}-${index}`}>
                  {tabConfig.columns.map((column) => {
                    if (["from", "to"].includes(column.key)) {
                      const fullAddress = row[`${column.key}Full`];
                      const lowerAddress = fullAddress?.toLowerCase?.();
                      const resolvedName = lowerAddress
                        ? addressNames[lowerAddress]
                        : null;

                      return (
                        <td key={column.key}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "end",
                              gap: "6px",
                            }}
                          >
                            <Tooltip
                              title={fullAddress || row[column.key] || "--"}
                              placement="top"
                              arrow
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  lineHeight: 1.1,
                                }}
                              >
                                {resolvedName && (
                                  <span
                                    style={{
                                      fontSize: "1rem",
                                      color: "#bfc3d9",
                                      marginBottom: "5px",
                                    }}
                                  >
                                    {resolvedName}
                                  </span>
                                )}
                                <span
                                  style={{
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {row[column.key] || "--"}
                                </span>
                              </div>
                            </Tooltip>
                            {fullAddress && (
                              <IconButton
                                className="totalPriceH2"
                                size="small"
                                onClick={() => copyToClipboard(fullAddress)}
                              >
                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </div>
                        </td>
                      );
                    }

                    return (
                      <td key={column.key}>
                        {column.key === "amountBnb" ? (
                          `${row[column.key]}`
                        ) : column.key === "hashId" ? (
                          row[column.key] ? (
                            <Tooltip
                              title={row[column.key]}
                              placement="top"
                              arrow
                            >
                              <a
                                href={`${BSC_TX_BASE_URL}${row[column.key]}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hash-link"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  color: "#fff",
                                  textDecoration: "none",
                                }}
                              >
                                {shortenAddress(row[column.key])}
                                <LaunchIcon
                                  sx={{ fontSize: 16, color: "#fff" }}
                                />
                              </a>
                            </Tooltip>
                          ) : (
                            "Loading..."
                          )
                        ) : (
                          row[column.key] || "--"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
        </div>
      </div>

      <Pagination
        totalList={data[activeTab].total}
        pages={totalPages}
        currentPage={currentPage}
        setCurrentPage={paginationSetPage}
        itemsPerPage={ITEMS_PER_PAGE}
      />
    </div>
  );
}

export default TransactionsTable;
